import { Quote, Invoice, InvoiceItem, Client } from '../types';
import { v4 as uuidv4 } from 'uuid';

export interface QuoteToInvoiceData {
  quote: Quote;
  dueDate: Date;
  notes?: string;
  terms?: string;
}

export const convertQuoteToInvoice = (data: QuoteToInvoiceData): Invoice => {
  const { quote, dueDate, notes, terms } = data;
  
  // Generate a new invoice number
  const invoiceNumber = generateInvoiceNumber();
  
  // Convert quote items to invoice items
  const invoiceItems: InvoiceItem[] = quote.items.map(item => ({
    id: uuidv4(),
    productId: item.id,
    description: item.description,
    quantity: item.quantity,
    unitPrice: item.unitPrice,
    taxRate: item.taxRate,
    taxAmount: item.total * (item.taxRate / 100),
    total: item.total
  }));

  // Create the invoice object
  const invoice: Invoice = {
    id: uuidv4(),
    invoiceNumber,
    clientId: quote.client.id,
    client: quote.client as Client,
    items: invoiceItems,
    subtotal: quote.subtotal,
    taxAmount: quote.taxAmount,
    discountAmount: quote.discountAmount,
    total: quote.total,
    status: 'draft',
    issueDate: new Date(),
    dueDate,
    notes: notes || quote.notes,
    terms: terms || quote.terms,
    createdBy: quote.createdBy,
    createdAt: new Date(),
    updatedAt: new Date()
  };

  return invoice;
};

export const generateInvoiceNumber = (): string => {
  const year = new Date().getFullYear();
  const month = String(new Date().getMonth() + 1).padStart(2, '0');
  const random = Math.floor(Math.random() * 1000).toString().padStart(3, '0');
  return `INV-${year}${month}-${random}`;
};

export const validateQuoteForConversion = (quote: Quote): { isValid: boolean; errors: string[] } => {
  const errors: string[] = [];

  if (quote.status === 'rejected') {
    errors.push('Cannot convert a rejected quote to invoice');
  }

  if (quote.status === 'expired') {
    errors.push('Cannot convert an expired quote to invoice');
  }

  if (quote.items.length === 0) {
    errors.push('Quote must have at least one item');
  }

  if (quote.total <= 0) {
    errors.push('Quote total must be greater than zero');
  }

  return {
    isValid: errors.length === 0,
    errors
  };
};
